import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';

export interface InvitationOut {
  id: string;
  email: string;
  name: string;
  role_name: string | null;
  invited_by_name: string | null;
  status: string;
  created_at: string;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
}

function invUrl(orgId: string) {
  return `/api/v1/organizations/${orgId}/invitations`;
}

export function useInvitations(status?: string) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    enabled: !!orgId,
    queryKey: ['invitations', orgId, status],
    queryFn: async () => {
      const params = status ? `?status=${status}` : '';
      const { data } = await clientApi.get<InvitationOut[]>(`${invUrl(orgId!)}${params}`);
      return data;
    },
  });
}

export function useRevokeInvitation() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invitationId: string) => {
      const { data } = await clientApi.post<InvitationOut>(
        `${invUrl(orgId!)}/${invitationId}/revoke`,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations', orgId] });
    },
  });
}

export function useResendInvitation() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invitationId: string) => {
      const { data } = await clientApi.post<InvitationOut>(
        `${invUrl(orgId!)}/${invitationId}/resend`,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations', orgId] });
    },
  });
}
